import { create } from 'zustand';
import type * as THREE from 'three';
import type {
  BatModel,
  DataGap,
  LengthUnit,
  RawProfilePoint,
  StatusEntry,
  StatusLevel,
} from '@/types/profile';
import { parseCFile } from '@/lib/parser/parseCFile';
import { parseTUR } from '@/lib/parser/parseTUR';
import { trimProfile } from '@/lib/parser/trimProfile';
import { normalizeProfile } from '@/lib/parser/normalizeProfile';
import { computeMetrics } from '@/lib/geometry/computeMetrics';
import { buildLatheGeometry } from '@/lib/geometry/buildLatheGeometry';
import { fillGapsFromDetail } from '@/lib/parser/fillGaps';

function describeGap(g: DataGap, unit: LengthUnit): string {
  const span = (g.toPosition - g.fromPosition).toFixed(1);
  return `Possible missing geometry: a ${span}${unit} gap between position ${g.fromPosition.toFixed(1)} and ${g.toPosition.toFixed(1)}, where radius jumps from ${g.fromRadius.toFixed(2)} to ${g.toRadius.toFixed(2)}${unit}. This region may have been shaped by a separate operation (knob forming, sanding, or cup routing) not included in this file — check with the CNC program for a file that includes it.`;
}

export type ActiveTab = 'viewer' | 'inspector' | 'info' | 'library';

interface ViewerOptions {
  wireframe: boolean;
  profileOverlay: boolean;
  sectionClipping: boolean;
  clipPosition: number; // 0..1 along the bat length
  displayUnit: LengthUnit;
}

interface AppState {
  model: BatModel | null;
  geometry: THREE.BufferGeometry | null;
  activeTab: ActiveTab;
  status: StatusEntry[];
  isProcessing: boolean;
  viewer: ViewerOptions;

  /** All files dropped alongside the primary one (e.g. .DAT/.U/.V/.SAN
   * dropped together with .TUR/.C) — kept so "Save to Library" can attach
   * everything, not just whichever file generated the geometry. */
  rawFiles: File[];

  /** Raw points from an optional companion detail file, used to fill detected gaps. */
  detailRawPoints: RawProfilePoint[] | null;
  detailFileName: string | null;

  /** Populated by Viewer3D once the mesh exists, used by the export pipeline. */
  meshRef: THREE.Mesh | null;
  canvasRef: HTMLCanvasElement | null;

  loadFileGroup: (files: File[]) => Promise<void>;
  loadDetailFile: (file: File) => Promise<void>;
  clearDetailFile: () => void;
  autoDetect: () => void;
  generateGeometry: () => void;
  reset: () => void;
  setActiveTab: (tab: ActiveTab) => void;
  pushStatus: (level: StatusLevel, message: string) => void;
  setViewerOption: <K extends keyof ViewerOptions>(key: K, value: ViewerOptions[K]) => void;
  setMeshRef: (mesh: THREE.Mesh | null) => void;
  setCanvasRef: (canvas: HTMLCanvasElement | null) => void;
  setGeometry: (geometry: THREE.BufferGeometry | null) => void;
}

function makeStatusEntry(level: StatusLevel, message: string): StatusEntry {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, level, message, timestamp: Date.now() };
}

function deriveModelName(fileName: string): string {
  return fileName.replace(/\.[a-z0-9]+$/i, '').trim() || 'Untitled Model';
}

/**
 * Runs trim -> (optional gap-fill from a companion file) -> normalize -> metrics,
 * pushing status messages along the way. Shared by loadFile, loadDetailFile, and
 * autoDetect so all three behave identically.
 */
function runPipeline(
  rawPoints: RawProfilePoint[],
  modelName: string,
  detailPoints: RawProfilePoint[] | null,
  detailFileName: string | null,
  pushStatus: (level: StatusLevel, message: string) => void,
) {
  const trim = trimProfile(rawPoints);
  trim.notes.forEach((note) => pushStatus('info', note));

  if (trim.trimmedPoints.length < 2) {
    return null;
  }

  pushStatus(
    trim.confidence > 0.6 ? 'success' : 'warning',
    `Auto-trim confidence: ${(trim.confidence * 100).toFixed(0)}%.`,
  );

  let finalPoints = trim.trimmedPoints;
  let gaps = trim.gaps;

  if (detailPoints && trim.gaps.length > 0) {
    const fillResult = fillGapsFromDetail(trim.trimmedPoints, trim.gaps, detailPoints);
    finalPoints = fillResult.points;
    gaps = [...fillResult.filledGaps, ...fillResult.unresolvedGaps];
    if (fillResult.filledGaps.length > 0) {
      pushStatus(
        'success',
        `Filled ${fillResult.filledGaps.length} gap(s) using "${detailFileName}" — reconstructed regions are marked distinctly and are estimates, not verified machine data.`,
      );
    }
    if (fillResult.unresolvedGaps.length > 0) {
      pushStatus(
        'warning',
        `${fillResult.unresolvedGaps.length} gap(s) could not be filled — "${detailFileName}" has no recorded data in that position range either.`,
      );
    }
  }

  const normalized = normalizeProfile(finalPoints);
  pushStatus('info', `Detected units: ${normalized.unit}.`);
  gaps.filter((g) => !g.filled).forEach((g) => pushStatus('warning', describeGap(g, normalized.unit)));

  const metrics = computeMetrics(normalized, modelName, normalized.unit);

  return { trim: { ...trim, gaps }, normalized, metrics };
}

export const useAppStore = create<AppState>((set, get) => ({
  model: null,
  geometry: null,
  activeTab: 'viewer',
  status: [makeStatusEntry('info', 'Ready. Drag and drop a Mark Lumber .C file to begin.')],
  isProcessing: false,
  viewer: {
    wireframe: false,
    profileOverlay: false,
    sectionClipping: false,
    clipPosition: 0.5,
    displayUnit: 'mm',
  },
  detailRawPoints: null,
  detailFileName: null,
  rawFiles: [],
  meshRef: null,
  canvasRef: null,

  pushStatus: (level, message) => {
    set((s) => ({ status: [...s.status, makeStatusEntry(level, message)].slice(-200) }));
  },

  setActiveTab: (tab) => set({ activeTab: tab }),

  setViewerOption: (key, value) =>
    set((s) => ({ viewer: { ...s.viewer, [key]: value } })),

  setMeshRef: (mesh) => set({ meshRef: mesh }),
  setCanvasRef: (canvas) => set({ canvasRef: canvas }),
  setGeometry: (geometry) => set({ geometry }),

  reset: () => {
    set({
      model: null,
      geometry: null,
      activeTab: 'viewer',
      detailRawPoints: null,
      detailFileName: null,
      rawFiles: [],
      status: [makeStatusEntry('info', 'Reset. Drag and drop a Mark Lumber .C file to begin.')],
    });
  },

  clearDetailFile: () => {
    set({ detailRawPoints: null, detailFileName: null });
    get().pushStatus('info', 'Detail file cleared.');
  },

  autoDetect: () => {
    const { model, detailRawPoints, detailFileName, pushStatus } = get();
    if (!model) {
      pushStatus('warning', 'Load a .C file before running Auto Detect.');
      return;
    }
    pushStatus('info', 'Re-running automatic lead-in/lead-out detection...');
    const result = runPipeline(model.raw.points, model.modelName, detailRawPoints, detailFileName, pushStatus);
    if (!result) {
      pushStatus('error', 'Unable to isolate a physical profile from this file.');
      return;
    }
    const { trim, normalized, metrics } = result;
    set((s) => ({
      model: s.model
        ? { ...s.model, trim, normalized, metrics, detailFileName: detailFileName ?? undefined }
        : s.model,
      viewer: { ...s.viewer, displayUnit: normalized.unit },
    }));
  },

  generateGeometry: () => {
    const { model, pushStatus } = get();
    if (!model) {
      pushStatus('warning', 'Load a .C file before generating a model.');
      return;
    }
    try {
      const { geometry } = buildLatheGeometry(model.normalized);
      set({ geometry });
      pushStatus('success', 'Generated watertight 3D mesh from the trimmed profile.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown geometry error.';
      pushStatus('error', `Failed to generate geometry: ${message}`);
    }
  },

  loadFileGroup: async (files: File[]) => {
    const { pushStatus, detailRawPoints, detailFileName } = get();
    if (files.length === 0) return;

    const turFile = files.find((f) => /\.tur$/i.test(f.name));
    const cFile = files.find((f) => /\.c$/i.test(f.name));
    const primaryFile = turFile ?? cFile;

    if (!primaryFile) {
      pushStatus('error', 'No .C or .TUR file found among the dropped files — at least one is required to generate geometry.');
      return;
    }

    set({ isProcessing: true, rawFiles: files });
    if (files.length > 1) {
      pushStatus('info', `Reading ${files.length} file(s), using "${primaryFile.name}" for geometry...`);
    } else {
      pushStatus('info', `Reading "${primaryFile.name}"...`);
    }

    try {
      const text = await primaryFile.text();
      const isTUR = primaryFile === turFile;

      let raw: ReturnType<typeof parseCFile>;
      if (isTUR) {
        const points = parseTUR(text);
        raw = {
          points,
          diagnostics: {
            delimiter: 'semicolon',
            decimalStyle: 'point',
            totalLines: text.split(/\r\n?|\n/).length,
            parsedRows: points.length,
            skippedRows: 0,
            skippedLineNumbers: [],
          },
        };
      } else {
        raw = parseCFile(text);
      }

      if (raw.points.length === 0) {
        pushStatus('error', 'No valid rows were found in this file.');
        set({ isProcessing: false });
        return;
      }

      if (isTUR) {
        pushStatus(
          'info',
          `Parsed ${raw.points.length} point(s) from the .TUR turning program (including interpolated arc geometry).`,
        );
      } else {
        pushStatus(
          'info',
          `Parsed ${raw.diagnostics.parsedRows} row(s) (delimiter: ${raw.diagnostics.delimiter}, decimals: ${raw.diagnostics.decimalStyle}). Skipped ${raw.diagnostics.skippedRows} invalid row(s).`,
        );
      }

      const modelName = deriveModelName(primaryFile.name);
      const result = runPipeline(raw.points, modelName, detailRawPoints, detailFileName, pushStatus);

      if (!result) {
        pushStatus('error', 'Unable to isolate a physical profile from this file.');
        set({ isProcessing: false });
        return;
      }
      const { trim, normalized, metrics } = result;

      const model: BatModel = {
        id: `${Date.now()}`,
        fileName: primaryFile.name,
        modelName,
        raw,
        trim,
        normalized,
        metrics,
        createdAt: Date.now(),
        detailFileName: detailFileName ?? undefined,
      };

      set((s) => ({
        model,
        isProcessing: false,
        viewer: { ...s.viewer, displayUnit: normalized.unit },
      }));

      get().generateGeometry();
      pushStatus('success', `Generated 3D bat model from "${primaryFile.name}".`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error while processing file.';
      pushStatus('error', `Failed to process file: ${message}`);
      set({ isProcessing: false });
    }
  },

  loadDetailFile: async (file: File) => {
    const { pushStatus, model } = get();
    pushStatus('info', `Reading detail file "${file.name}"...`);
    try {
      const text = await file.text();
      const raw = parseCFile(text);

      if (raw.points.length === 0) {
        pushStatus('error', `No valid Position;Radius rows were found in "${file.name}".`);
        return;
      }

      set({ detailRawPoints: raw.points, detailFileName: file.name });
      pushStatus(
        'success',
        `Loaded detail file "${file.name}" (${raw.points.length} points).`,
      );

      // If a model is already loaded, automatically re-run the pipeline so the
      // detail file takes effect immediately without a manual extra step.
      if (model) {
        get().autoDetect();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error while processing file.';
      pushStatus('error', `Failed to process detail file: ${message}`);
    }
  },
}));
